/**
 * Returning-player gate: move existing team into this campaign.
 * Any active roster member can continue; on successful pay they become captain.
 */
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import RegistrationPartnerAndUpsells from './RegistrationPartnerAndUpsells';

const Box = styled.div`
  margin: 0 0 1.25rem;
`;

const Title = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const Help = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
`;

const Notice = styled.div`
  margin: 0.75rem 0 1rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e3a5f;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const RadioRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin: 0 0 0.65rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #334155;
  cursor: pointer;
  input {
    margin-top: 0.2rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  margin: 0.5rem 0 1rem;
  background: #fff;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  margin: 0.35rem 0 0;
  box-sizing: border-box;
`;

const Label = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  margin: 1rem 0 0.35rem;
`;

const SubBox = styled.div`
  margin-top: 0.75rem;
  padding: 1rem 1.05rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const PartnerList = styled.ul`
  margin: 0 0 0.75rem;
  padding-left: 1.1rem;
  font-size: 0.88rem;
  color: #334155;
  line-height: 1.45;
`;

export default function ContinueTeamSetupStep({
  reusableTeams,
  tenantId,
  seasonId,
  value,
  onChange,
  partnerUpsell,
  onPartnerUpsellChange,
}) {
  const { t } = useTranslation();
  const pt = 'register.continueTeam';
  const list = Array.isArray(reusableTeams) ? reusableTeams : [];

  const decision = value?.decision || null; // 'continue' | 'new'
  const teamId = value?.teamId || list[0]?.teamId || '';
  const partnerMode = value?.partnerMode || 'keep';
  const removedIds = value?.removedIds || [];
  const teamName = value?.teamName || '';

  const selected = useMemo(
    () => list.find((tm) => String(tm.teamId) === String(teamId)) || null,
    [list, teamId]
  );
  const viewerIsCaptain = selected?.isCaptain === true;
  // Everyone else on the roster (current captain included when viewer is a member).
  const partners = (selected?.members || []).filter((m) => m.isSelf !== true);

  const patch = (partial) => onChange({ ...(value || {}), ...partial });

  useEffect(() => {
    if (!teamId && list[0]?.teamId) {
      patch({
        teamId: list[0].teamId,
        teamName: list[0].teamName || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useEffect(() => {
    if (selected && decision === 'continue') {
      if (!teamName || teamName === value?.previousPrefill) {
        patch({
          teamName: selected.teamName || '',
          previousPrefill: selected.teamName || '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.teamId, decision]);

  const toggleRemove = (playerId) => {
    const set = new Set(removedIds.map(String));
    const id = String(playerId);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    patch({ removedIds: Array.from(set) });
  };

  const showNewPartner = decision === 'continue' && partnerMode !== 'keep';

  return (
    <Box>
      <Title>{t(`${pt}.title`, 'Bring a previous team?')}</Title>
      <Help>
        {t(
          `${pt}.help`,
          'We recognized you already played in this league. You can move the same team (stats and history kept) into this campaign, or start a brand-new team.'
        )}
      </Help>

      <RadioRow>
        <input
          type="radio"
          name="continueDecision"
          checked={decision === 'continue'}
          onChange={() =>
            patch({
              decision: 'continue',
              teamId: teamId || list[0]?.teamId || '',
              teamName: selected?.teamName || list[0]?.teamName || teamName,
              previousPrefill: selected?.teamName || list[0]?.teamName || '',
            })
          }
        />
        <span>
          {t(
            `${pt}.moveYes`,
            'Yes — move my team from a previous season into this campaign'
          )}
        </span>
      </RadioRow>
      <RadioRow>
        <input
          type="radio"
          name="continueDecision"
          checked={decision === 'new'}
          onChange={() => patch({ decision: 'new' })}
        />
        <span>{t(`${pt}.moveNo`, 'No — create a brand-new team')}</span>
      </RadioRow>

      {decision === 'continue' ? (
        <SubBox>
          {list.length > 1 ? (
            <>
              <Label>{t(`${pt}.pickTeam`, 'Which team?')}</Label>
              <Select
                value={teamId}
                onChange={(e) => {
                  const next = list.find((tm) => String(tm.teamId) === String(e.target.value));
                  patch({
                    teamId: e.target.value,
                    teamName: next?.teamName || '',
                    previousPrefill: next?.teamName || '',
                    partnerMode: 'keep',
                    removedIds: [],
                  });
                }}
              >
                {list.map((tm) => (
                  <option key={tm.teamId} value={tm.teamId}>
                    {tm.teamName}
                    {tm.isCaptain === false
                      ? t(`${pt}.memberSuffix`, ' (you are a member)')
                      : ''}
                  </option>
                ))}
              </Select>
            </>
          ) : selected ? (
            <Help style={{ marginBottom: '0.75rem' }}>
              {t(`${pt}.teamLabel`, 'Team')}: <strong>{selected.teamName}</strong>
            </Help>
          ) : null}

          {!viewerIsCaptain ? (
            <Notice>
              {t(
                `${pt}.memberBecomesCaptain`,
                'You are currently a member on this team. If you continue and pay, you become the captain for this campaign. Other roster members (including the previous captain) will be asked to pay their League Pass, unless you remove them below.'
              )}
            </Notice>
          ) : null}

          <Label>{t(`${pt}.partnersTitle`, 'Same partner(s)?')}</Label>
          {partners.length > 0 ? (
            <PartnerList>
              {partners.map((m) => (
                <li key={m.playerId}>
                  {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                  {m.isCaptain ? t(`${pt}.captainSuffix`, ' · captain') : ''}
                  {m.skillLevel != null ? ` · skill ${m.skillLevel}` : ''}
                </li>
              ))}
            </PartnerList>
          ) : (
            <Help>{t(`${pt}.noPartners`, 'No active partners on this team right now.')}</Help>
          )}

          <RadioRow>
            <input
              type="radio"
              name="continuePartnerMode"
              checked={partnerMode === 'keep'}
              onChange={() => patch({ partnerMode: 'keep', removedIds: [] })}
            />
            <span>
              {t(
                `${pt}.partnerKeep`,
                'Yes — keep the same partner(s). They will be asked to pay their package.'
              )}
            </span>
          </RadioRow>
          <RadioRow>
            <input
              type="radio"
              name="continuePartnerMode"
              checked={partnerMode === 'solo'}
              onChange={() => patch({ partnerMode: 'solo', removedIds: [] })}
            />
            <span>
              {t(
                `${pt}.partnerSolo`,
                'No — continue without them for this edition (history kept)'
              )}
            </span>
          </RadioRow>
          {partners.length > 0 ? (
            <RadioRow>
              <input
                type="radio"
                name="continuePartnerMode"
                checked={partnerMode === 'pick'}
                onChange={() => patch({ partnerMode: 'pick' })}
              />
              <span>{t(`${pt}.partnerPick`, 'Remove specific partners')}</span>
            </RadioRow>
          ) : null}
          {partnerMode === 'pick' &&
            partners.map((m) => (
              <RadioRow key={m.playerId} style={{ marginLeft: '1.1rem' }}>
                <input
                  type="checkbox"
                  checked={removedIds.map(String).includes(String(m.playerId))}
                  onChange={() => toggleRemove(m.playerId)}
                />
                <span>
                  {t(`${pt}.removeNamed`, 'Remove {{name}}', {
                    name: [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email,
                  })}
                </span>
              </RadioRow>
            ))}

          {showNewPartner ? (
            <>
              <Label>{t(`${pt}.newPartner`, 'Invite a new partner (optional)')}</Label>
              <RegistrationPartnerAndUpsells
                tenantId={tenantId}
                seasonId={seasonId}
                value={partnerUpsell}
                onChange={onPartnerUpsellChange}
                sections="partner"
              />
            </>
          ) : null}

          <Label>{t(`${pt}.teamNameTitle`, 'Team name')}</Label>
          <Help style={{ marginBottom: '0.35rem' }}>
            {t(
              `${pt}.teamNameHelp`,
              'Keep the same name (pre-filled) or change it for this campaign.'
            )}
          </Help>
          <Input
            value={teamName}
            onChange={(e) => patch({ teamName: e.target.value })}
            placeholder={t(`${pt}.teamNamePlaceholder`, 'Team name')}
            minLength={3}
          />
        </SubBox>
      ) : null}
    </Box>
  );
}

export function buildContinuePartnerPlan(setup) {
  if (!setup || setup.decision !== 'continue') return 'keep';
  if (setup.partnerMode === 'solo') return 'solo';
  if (setup.partnerMode === 'pick') {
    return { remove_player_ids: Array.isArray(setup.removedIds) ? setup.removedIds : [] };
  }
  return 'keep';
}

/** @deprecated Members may continue; kept for import compatibility (always false). */
export function isContinueAsNonCaptain() {
  return false;
}
